import { Capacitor } from '@capacitor/core';
import { LDFlagSet } from 'launchdarkly-js-sdk-common';
import pkg from '../../package.json';

import { datadogRum } from '@datadog/browser-rum';
import isRunningChromatic from 'chromatic/isChromatic';
import { UserSource } from '../api/user/types';
import ImportedFeatureConfig from '../config/featureConfig';

export type Platform = 'web' | 'ios' | 'android';

type Environment = 'test' | 'dev' | 'next' | 'live';

// webpack5 no longer includes polyfill for destructuring multiple values from process.env
// see https://github.com/webpack/webpack/issues/14800

const NODE_ENV = process.env.NODE_ENV;
const VITE_API_HOST = process.env.VITE_API_HOST;
const VITE_STRIPE_API_KEY = process.env.VITE_STRIPE_API_KEY || process.env.STORYBOOK_STRIPE_API_KEY;
const VITE_STRIPE_API_SECRET_KEY =
  process.env.VITE_STRIPE_API_SECRET_KEY || process.env.STORYBOOK_STRIPE_API_SECRET_KEY;
const VITE_STRIPE_API_KEY_SABAL = process.env.VITE_STRIPE_API_KEY_SABAL;
const VITE_PALMETTO_WEB_URL = process.env.VITE_PALMETTO_WEB_URL;
const VITE_SEGMENT_API_KEY = process.env.VITE_SEGMENT_API_KEY;
const VITE_INTERCOM_APP_ID = process.env.VITE_INTERCOM_APP_ID;
const STORYBOOK = process.env.STORYBOOK;
const VITE_ARCADIA_ORG_ID = process.env.VITE_ARCADIA_ORG_ID;
const VITE_CUSTOMER_IO_API_KEY = process.env.VITE_CUSTOMER_IO_API_KEY;
const VITE_ARCADIA_CONNECT_URL = process.env.VITE_ARCADIA_CONNECT_URL;
const VITE_GTAG_ID = process.env.VITE_GTAG_ID;
const VITE_GA_API_KEY = process.env.VITE_GA_API_KEY;

// UA checks preserve Ionic isPlatform semantics: mobile-Safari/mobile-Chrome users
// count as ios/android even outside the Capacitor shell (iPadOS reports as MacIntel).
const capacitorPlatform = Capacitor.getPlatform();
const userAgent = window.navigator?.userAgent ?? '';

const isNative = Capacitor.isNativePlatform();
const isIOS =
  capacitorPlatform === 'ios' ||
  /iPad|iPhone|iPod/.test(userAgent) ||
  (window.navigator?.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
const isAndroid = capacitorPlatform === 'android' || /android/i.test(userAgent);

let platform: Platform;
let appId;
if (isIOS) {
  platform = 'ios';
  appId = 'com.palmetto.palmettodev';
} else if (isAndroid) {
  platform = 'android';
  appId = 'com.palmetto.home';
} else {
  platform = 'web';
}

const isProductionBuild = NODE_ENV === 'production';
const isTestRun = NODE_ENV === 'test';
const isStorybook = STORYBOOK === 'true';
const isChromatic = isRunningChromatic();

const baseApiUrl =
  VITE_API_HOST || (isProductionBuild ? 'https://home.palmetto.com' : 'https://home-next.palmetto.com');

const palmettoWebUrl =
  VITE_PALMETTO_WEB_URL || (isProductionBuild ? 'https://palmetto.com' : 'https://next.palmetto.com');

const { hostname } = new URL(baseApiUrl);

let environment: Environment;
if (isTestRun || isStorybook) {
  environment = 'test';
} else if (isProductionBuild && hostname === 'home.palmetto.com') {
  environment = 'live';
} else if (isProductionBuild && hostname === 'home-next.palmetto.com') {
  environment = 'next';
} else {
  environment = 'dev';
}

const isLive = environment === 'live';
const isNext = environment === 'next';
const isDev = environment === 'dev';

export enum LDFlagKeys {
  pointOfContact = 'pointOfContact',
  referAFriend = 'referAFriend',
  servicePage = 'servicePage',
  utilityRatePlanActionItem = 'utilityRatePlanActionItem',
  bypassBankValidationErrorThreshold = 'bypassBankValidationErrorThreshold',
  referAFriendOrganizations = 'referAFriendOrganizations',
  utilitiesEligibleForRatePlan = 'utilitiesEligibleForRatePlan',
  energyPage = 'energyPage',
  energyCarousel = 'energyCarousel',
  weeklySummary = 'weeklySummary',
  coupons = 'coupons',
  displayPointsOnArticlePages = 'displayPointsOnArticlePages',
  homePage = 'homePage',
  tasks = 'tasks',
  claimPointsArticleWidget = 'claimPointsArticleWidget',
  connectedAccountsPage = 'connectedAccountsPage',
  splineHouse = 'splineHouse',
  pointsHistory = 'pointsHistory',
  solarSavings = 'solarSavings',
  solarValue = 'solarValue',
  userEmailUpdate = 'userEmailUpdate',
  showGetAccountOnLoginSignupChoice = 'showGetAccountOnLoginSignupChoice',
  projectPageAsHome = 'projectPageAsHome',
  riveBottomNav = 'riveBottomNav',
  plaidDemo = 'plaidDemo',
  showRateDataForSavings = 'showRateDataForSavings',
  compareMultiplePeriods = 'compareMultiplePeriods',
  sunnyChatEnabled = 'sunnyChatEnabled',
  demoMode = 'demoMode',
  achievementsPage = 'achievementsPage',
  homePageV2 = 'homePageV2',
  notificationCenter = 'notificationCenter',
  timeOfDayPreview = 'timeOfDayPreview',
}

export type LDFlags = LDFlagSet & {
  [key in LDFlagKeys]?: boolean | string | string[] | Record<string, unknown>;
};

export const PlatformConfig: {
  platform: Platform; // web, ios, android
  environment: Environment; // test, dev, next, live
  isNative: boolean; // shortcut for ios or android as platform
  isIOS: boolean; // shortcut for ios as platform
  isAndroid: boolean; // shortcut for android as platform
  isProductionBuild: boolean; // is this a production build?
  isTestRun: boolean; // is this a test run?
  isStorybook: boolean; // is this running in Storybook?
  isChromatic: boolean; // is this running in Chromatic
  isLive: boolean; // is this running in the live environment?
  isNext: boolean; // is this running in the next environment?
  isDev: boolean; // is this running in the dev environment?
  appId?: string; // native app ID (e.g. com.palmetto.home)
  hostname: string; // expected web hostname (native hostname is always localhost)
  baseApiUrl: string; // web host URL — not used for API routing (proxy handles that); used for environment detection and Datadog tracing
  palmettoWebUrl: string; // the base URL for palmetto.com
} = {
  platform,
  environment,
  isNative,
  isIOS,
  isAndroid,
  isProductionBuild,
  isTestRun,
  isStorybook,
  isChromatic,
  isLive,
  isNext,
  isDev,
  appId,
  hostname,
  baseApiUrl,
  palmettoWebUrl,
};

if (!isTestRun) {
  if (!VITE_STRIPE_API_KEY) {
    datadogRum.addError('VITE_STRIPE_API_KEY is missing');
  }
  if (!VITE_STRIPE_API_KEY_SABAL) {
    datadogRum.addError('VITE_STRIPE_API_KEY is missing');
  }
}

export const FeatureConfig = ImportedFeatureConfig;

export const StripeConfig: {
  apiKey: string;
  apiSecretKey: string;
  apiKeySabal: string;
} = {
  apiKey: VITE_STRIPE_API_KEY || '',
  apiSecretKey: VITE_STRIPE_API_SECRET_KEY || '',
  apiKeySabal: VITE_STRIPE_API_KEY_SABAL || '',
};

export const SegmentConfig = {
  apiKey: VITE_SEGMENT_API_KEY || '',
};

export const SABAL_FINANCING_SOURCES = new Set([UserSource.palmettoPPA, UserSource.epc]);

export const ONBOARDING_REQUIRED_USER_SOURCES = new Set<UserSource>([UserSource.organic, UserSource.tcd]);

export const intercomConfig = {
  appId: VITE_INTERCOM_APP_ID || '',
};

export const ArcadiaConfig = {
  orgId: VITE_ARCADIA_ORG_ID || '',
  connectUrl: VITE_ARCADIA_CONNECT_URL || '',
};

export const customerIOConfig = {
  apiKey: VITE_CUSTOMER_IO_API_KEY || '',
};

export const GoogleAnalyticsConfig = {
  measurementId: VITE_GTAG_ID || '',
  apiKey: VITE_GA_API_KEY || '',
};

export const coreLibConfig: import('@helios/frontend-core').CoreLibConfig = {
  platform: PlatformConfig.platform,
  hostname: PlatformConfig.hostname,
  isNative: PlatformConfig.isNative,
  isIOS: PlatformConfig.isIOS,
  isAndroid: PlatformConfig.isAndroid,
  isProductionBuild: PlatformConfig.isProductionBuild,
  isTestRun: PlatformConfig.isTestRun,
  isStorybook: PlatformConfig.isStorybook,
  isChromatic: PlatformConfig.isChromatic,
  appId: PlatformConfig.appId,
  segmentApiKey: SegmentConfig.apiKey,
  customerIoApiKey: customerIOConfig.apiKey,
  googleAnalyticsMeasurementId: GoogleAnalyticsConfig.measurementId,
  googleAnalyticsApiKey: GoogleAnalyticsConfig.apiKey,
  appVersion: pkg.version,
};

export const getAppDownloadLink = (): string => {
  const userAgent = navigator?.userAgent;
  if (userAgent?.match(/iPhone|iPad|iPod/i)) {
    return 'https://apps.apple.com/us/app/the-palmetto-app/id1198633065';
  }
  if (userAgent?.match(/Android/i)) {
    return 'https://play.google.com/store/apps/details?id=com.palmetto.home';
  }
  return 'https://palmetto.com/app-download';
};
